import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { NotesActions } from '../notesSelector/hooks';

export type NoteAnimationAction =
  | 'Move'
  | 'AddNote'
  | 'NotesMerge'
  | 'Edit'
  | 'MultiEdit'
  | 'SplitNote';

export type NoteAnimationData = {
  id: string;
  action: NoteAnimationAction;
  folder: NotesActions;
};
export type NoteAnimationContextType = {
  notesToAnimate: NoteAnimationData[];
  setNotesToAnimate: React.Dispatch<React.SetStateAction<NoteAnimationData[]>>;
  hasAnimation: (noteId: string) => NoteAnimationData | undefined;
};

export const NoteAnimationContext = createContext<NoteAnimationContextType | null>(
  null
);

export const useNotesAnimationContext = (): NoteAnimationContextType => {
  const value = useContext(NoteAnimationContext);

  if (!value) {
    throw new Error('should be called in NoteAnimationContextProvider scope');
  }
  return value;
};

export const NoteAnimationContextProvider: React.FC = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const [notesToAnimate, setNotesToAnimate] = useState<NoteAnimationData[]>([]);

  const hasAnimation = useCallback(
    (noteId: string) => notesToAnimate.find((n) => n.id === noteId),
    [notesToAnimate]
  );

  const value = useMemo(
    () => ({
      notesToAnimate,
      setNotesToAnimate,
      hasAnimation
    }),
    [hasAnimation, notesToAnimate]
  );

  return (
    <NoteAnimationContext.Provider value={value}>
      {children}
    </NoteAnimationContext.Provider>
  );
};
